/**
 * Utilidades para manejo de tienda de ropa
 * Estructura: users/{uid}/tienda-ropa/{prenda}/{fotos}
 */

export interface ClothingItem {
  id: string;
  name: string; // ej: "babucha TINA"
  slug: string; // ej: "babucha-tina"
  createdAt: string;
  variants: ClothingVariant[];
}

export interface ClothingVariant {
  id: string;
  name: string; // ej: "Rojo", "Azul"
  color: string;
  isMain: boolean; // true para la primera variante
  images: ClothingImage[];
  createdAt: string;
}

export interface ClothingImage {
  id: string;
  type: 'frontal' | 'reverso' | 'closeup1' | 'closeup2' | 'closeup3';
  url: string;
  path: string;
  name: string;
  size: number;
  uploadedAt: string;
}

/**
 * Crear slug a partir del nombre de la prenda
 */
export const createSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

/**
 * Generar nombre descriptivo para archivo de imagen
 */
export const generateImageFileName = (
  clothingName: string,
  variantColor: string,
  imageType: 'frontal' | 'reverso' | 'closeup1' | 'closeup2' | 'closeup3'
): string => {
  // Limpiar nombres
  const cleanClothingName = clothingName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
  
  const cleanColor = variantColor
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
  
  // Mapear tipos de imagen a nombres descriptivos
  const typeNames = {
    'frontal': 'frente',
    'reverso': 'reverso',
    'closeup1': 'detalle-1',
    'closeup2': 'detalle-2',
    'closeup3': 'detalle-3'
  };
  
  // Generar nombre: prenda-color-tipo
  return `${cleanClothingName}-${cleanColor}-${typeNames[imageType]}`;
};

/**
 * Subir imagen de prenda a Storage
 */
export const uploadClothingImage = async (
  file: File,
  userId: string,
  clothingSlug: string,
  variantId: string,
  imageType: 'frontal' | 'reverso' | 'closeup1' | 'closeup2' | 'closeup3',
  clothingName?: string,
  variantColor?: string
): Promise<{ success: boolean; url?: string; path?: string; error?: string }> => {
  try {
    if (!window.auth || !window.auth.storage || !window.auth.ref || !window.auth.uploadBytes || !window.auth.getDownloadURL) {
      throw new Error('Firebase Storage no está disponible');
    }

    const { storage, ref, uploadBytes, getDownloadURL } = window.auth;
    
    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      throw new Error('Solo se permiten archivos de imagen');
    }
    
    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('La imagen no puede superar los 5MB');
    }
    
    // Generar nombre descriptivo para el archivo
    let fileName: string;
    
    if (clothingName && variantColor) {
      // Usar nombres descriptivos: prenda-color-tipo
      const baseName = generateImageFileName(clothingName, variantColor, imageType);
      const fileExtension = file.name.split('.').pop() || 'jpg';
      fileName = `${baseName}.${fileExtension}`;
    } else {
      // Fallback: usar timestamp + nombre original
      const timestamp = Date.now();
      fileName = `${timestamp}_${file.name}`;
    }
    
    const imagePath = `users/${userId}/tienda-ropa/${clothingSlug}/${fileName}`;
    const imageRef = ref(storage, imagePath);
    
    // Subir archivo
    const snapshot = await uploadBytes(imageRef, file);
    
    // Obtener URL de descarga
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return {
      success: true,
      url: downloadURL,
      path: imagePath
    };
    
  } catch (error) {
    console.error('Error subiendo imagen de prenda:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error desconocido'
    };
  }
};

/**
 * Guardar prenda en Firestore
 */
export const saveClothingItem = async (
  userId: string,
  clothingItem: Omit<ClothingItem, 'id' | 'createdAt'>
): Promise<{ success: boolean; id?: string; error?: string }> => {
  try {
    if (!window.auth || !window.auth.db || !window.auth.doc || !window.auth.setDoc || !window.auth.serverTimestamp) {
      throw new Error('Firebase Firestore no está disponible');
    }

    const { db, doc, setDoc, serverTimestamp } = window.auth;
    
    const clothingData = {
      ...clothingItem,
      createdAt: serverTimestamp()
    };

    const clothingRef = doc(db, 'users', userId, 'clothing', clothingItem.slug);
    await setDoc(clothingRef, clothingData);
    
    return {
      success: true,
      id: clothingItem.slug
    };
    
  } catch (error) {
    console.error('Error guardando prenda:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error desconocido'
    };
  }
};

/**
 * Obtener todas las prendas del usuario
 */
export const getUserClothingItems = async (userId: string): Promise<ClothingItem[]> => {
  try {
    if (!window.auth || !window.auth.db || !window.auth.collection || !window.auth.getDocs) {
      throw new Error('Firebase Firestore no está disponible');
    }

    const { db, collection, getDocs } = window.auth;
    
    const clothingRef = collection(db, 'users', userId, 'clothing');
    const snapshot = await getDocs(clothingRef);
    
    const clothingItems: ClothingItem[] = [];
    snapshot.forEach((doc: any) => {
      const data = doc.data();
      clothingItems.push({
        id: doc.id,
        name: data.name || '',
        slug: data.slug || '',
        variants: data.variants || [],
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
      } as ClothingItem);
    });
    
    // Ordenar por fecha de creación (más recientes primero)
    clothingItems.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    return clothingItems;
    
  } catch (error) {
    console.error('Error obteniendo prendas:', error);
    return [];
  }
};

/**
 * Obtener imágenes de una variante específica
 */
export const getVariantImages = async (
  userId: string,
  clothingSlug: string,
  variantId: string
): Promise<ClothingImage[]> => {
  try {
    if (!window.auth || !window.auth.storage || !window.auth.ref || !window.auth.listAll || !window.auth.getMetadata || !window.auth.getDownloadURL) {
      throw new Error('Firebase Storage no está disponible');
    }

    const { storage, ref, listAll, getMetadata, getDownloadURL } = window.auth;
    
    const clothingRef = ref(storage, `users/${userId}/tienda-ropa/${clothingSlug}`);
    const result = await listAll(clothingRef);
    
    const images: ClothingImage[] = [];
    
    for (const itemRef of result.items) {
      try {
        const metadata = await getMetadata(itemRef);
        const url = await getDownloadURL(itemRef);
        
        // Determinar tipo de imagen por el nombre del archivo
        const fileName = itemRef.name.toLowerCase();
        let imageType: 'frontal' | 'reverso' | 'closeup1' | 'closeup2' | 'closeup3';
        
        // Detectar por nombres descriptivos (nueva estructura)
        if (fileName.includes('-frente') || fileName.includes('frente')) {
          imageType = 'frontal';
        } else if (fileName.includes('-reverso') || fileName.includes('reverso')) {
          imageType = 'reverso';
        } else if (fileName.includes('-detalle-1') || fileName.includes('detalle-1')) {
          imageType = 'closeup1';
        } else if (fileName.includes('-detalle-2') || fileName.includes('detalle-2')) {
          imageType = 'closeup2';
        } else if (fileName.includes('-detalle-3') || fileName.includes('detalle-3')) {
          imageType = 'closeup3';
        } 
        // Fallback para nombres antiguos
        else if (fileName.includes('frontal') || fileName.includes('front')) {
          imageType = 'frontal';
        } else if (fileName.includes('reverso') || fileName.includes('back')) {
          imageType = 'reverso';
        } else if (fileName.includes('closeup1') || fileName.includes('detail1')) {
          imageType = 'closeup1';
        } else if (fileName.includes('closeup2') || fileName.includes('detail2')) {
          imageType = 'closeup2';
        } else if (fileName.includes('closeup3') || fileName.includes('detail3')) {
          imageType = 'closeup3';
        } else {
          imageType = 'frontal'; // fallback
        }
        
        images.push({
          id: itemRef.name,
          type: imageType,
          url: url,
          path: itemRef.fullPath,
          name: metadata.name,
          size: metadata.size,
          uploadedAt: new Date(metadata.timeCreated).toISOString()
        });
      } catch (error) {
        console.warn(`Error obteniendo metadata de ${itemRef.name}:`, error);
      }
    }
    
    return images;
    
  } catch (error) {
    console.error('Error obteniendo imágenes de variante:', error);
    return [];
  }
};

/**
 * Eliminar imagen de Storage y Firestore
 */
export const deleteClothingImage = async (
  imagePath: string, 
  userId: string, 
  clothingSlug: string, 
  variantId: string, 
  imageId: string
): Promise<boolean> => {
  try {
    if (!window.auth || !window.auth.storage || !window.auth.ref || !window.auth.deleteObject || 
        !window.auth.db || !window.auth.doc || !window.auth.getDoc || !window.auth.setDoc) {
      throw new Error('Firebase no está disponible');
    }

    const { storage, ref, deleteObject, db, doc, getDoc, setDoc } = window.auth;

    // 1. Eliminar de Storage
    const imageRef = ref(storage, imagePath);
    await deleteObject(imageRef);
    console.log('✅ Imagen eliminada de Storage:', imagePath);

    // 2. Eliminar referencia de Firestore
    const clothingRef = doc(db, 'users', userId, 'clothing', clothingSlug);
    const clothingDoc = await getDoc(clothingRef);
    
    if (clothingDoc.exists()) {
      const clothingData = clothingDoc.data();
      
      // Buscar y eliminar la imagen de la variante correspondiente
      const updatedVariants = clothingData.variants.map((variant: any) => {
        if (variant.id === variantId) {
          return {
            ...variant,
            images: variant.images.filter((img: any) => img.id !== imageId)
          };
        }
        return variant;
      });

      // Actualizar el documento con las variantes sin la imagen eliminada
      await setDoc(clothingRef, {
        ...clothingData,
        variants: updatedVariants
      }, { merge: true });
      
      console.log('✅ Referencia de imagen eliminada de Firestore');
    }

    return true;
    
  } catch (error) {
    console.error('❌ Error eliminando imagen:', error);
    return false;
  }
};

/**
 * Verificar configuración de tienda
 */
export const getStoreConfig = async (userId: string): Promise<{ type: string; name: string } | null> => {
  try {
    if (!window.auth || !window.auth.db || !window.auth.doc || !window.auth.getDoc) {
      throw new Error('Firebase Firestore no está disponible');
    }

    const { db, doc, getDoc } = window.auth;
    
    const configRef = doc(db, 'users', userId, 'store', 'config');
    const configSnap = await getDoc(configRef);
    
    if (configSnap.exists()) {
      const data = configSnap.data();
      return {
        type: data.type,
        name: data.name
      };
    }
    
    return null;
    
  } catch (error) {
    console.error('Error obteniendo configuración de tienda:', error);
    return null;
  }
};

// Exportar para uso en consola del navegador
/**
 * Obtener imágenes de una variante desde Firestore (función mejorada)
 */
export const getVariantImagesFromFirestore = async (
  userId: string,
  clothingSlug: string,
  variantId: string
): Promise<ClothingImage[]> => {
  try {
    if (!window.auth || !window.auth.db || !window.auth.doc || !window.auth.getDoc) {
      throw new Error('Firebase Firestore no está disponible');
    }

    const { db, doc, getDoc } = window.auth;
    
    // Cargar desde Firestore
    const clothingRef = doc(db, 'users', userId, 'clothing', clothingSlug);
    const clothingDoc = await getDoc(clothingRef);
    
    if (clothingDoc.exists()) {
      const clothingData = clothingDoc.data();
      const variant = clothingData.variants?.find((v: any) => v.id === variantId);
      
      if (variant && variant.images && variant.images.length > 0) {
        console.log('✅ Imágenes cargadas desde Firestore:', variant.images.length);
        return variant.images.map((img: any) => ({
          id: img.id,
          type: img.type,
          url: img.url,
          path: img.path,
          name: img.name,
          size: img.size,
          uploadedAt: img.uploadedAt
        }));
      }
    }

    console.log('⚠️ No hay imágenes en Firestore para esta variante');
    return [];
    
  } catch (error) {
    console.error('Error obteniendo imágenes de Firestore:', error);
    return [];
  }
};

if (typeof window !== 'undefined') {
  (window as any).clothingStore = {
    uploadClothingImage,
    saveClothingItem,
    getUserClothingItems,
    getVariantImages,
    getVariantImagesFromFirestore,
    deleteClothingImage,
    getStoreConfig,
    createSlug
  };
}
